import { forwardRef, useEffect, useState, type ChangeEvent, type FocusEvent } from 'react'
import { Input, type InputProps } from './Input'

export interface TimeInputProps extends Omit<InputProps, 'value' | 'onChange' | 'onBlur'> {
  /** Current value, expected as ``HH:MM`` (also accepts an empty string). */
  value: string
  /** Fires after each keystroke, normalised. May be partial (``11:0``). */
  onChange: (next: string) => void
  /** Fires on blur. Always full ``HH:MM`` (or empty if untouched). */
  onBlur?: (next: string) => void
}

/**
 * Time-of-day input with progressive ``HH:MM`` masking.
 *
 *   "1"   →  "1"
 *   "11"  →  "11"
 *   "11" + "0"  →  "11:0"
 *   "11" + "00" →  "11:00"
 *
 * On blur, partial input is right-padded with zeros (``9`` → ``09:00``,
 * ``11`` → ``11:00``). When the final value isn't a valid ``HH:MM``
 * (e.g. ``25:99``), the underlying ``Input`` paints its red error border —
 * the parent decides whether the field is in an error state via the
 * ``error`` prop, which we forward verbatim.
 */
export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(
  { value, onChange, onBlur, error, ...rest },
  ref,
) {
  // Display state mirrors props but is decoupled while the user is mid-typing
  // so we don't fight against React's controlled input churn (caret jumping,
  // mid-keystroke parent re-renders).
  const [display, setDisplay] = useState(() => formatPartial(value))

  useEffect(() => {
    setDisplay((prev) => (sameMaskedValue(prev, value) ? prev : formatPartial(value)))
  }, [value])

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const masked = formatPartial(e.target.value)
    setDisplay(masked)
    onChange(masked)
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    const padded = padToFull(e.target.value)
    setDisplay(padded)
    onChange(padded)
    onBlur?.(padded)
  }

  // Spread ``error`` only when defined — Input declares ``error?: boolean``
  // without ``| undefined`` (exactOptionalPropertyTypes), so passing
  // ``error={undefined}`` directly is a type error.
  const errorProp = error ? { error: true } : {}
  return (
    <Input
      ref={ref}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      inputMode="numeric"
      maxLength={5}
      placeholder="HH:MM"
      {...errorProp}
      {...rest}
    />
  )
})

/** Strip non-digits, keep at most 4, insert ``:`` after the hour pair. */
function formatPartial(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}:${digits.slice(2)}`
}

/** Pad partial input to a full ``HH:MM`` by appending zeros on the right.
 *
 * Per the spec: blurring with ``1`` becomes ``10:00`` (literal append of
 * ``0:00``), ``11`` becomes ``11:00``, ``143`` becomes ``14:30``. Invalid
 * values like ``50:00`` (from ``5``) are passed through and the parent's
 * regex check turns the field red. */
function padToFull(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 0) return ''
  const padded = digits.padEnd(4, '0')
  return `${padded.slice(0, 2)}:${padded.slice(2)}`
}

function sameMaskedValue(a: string, b: string): boolean {
  return formatPartial(a) === formatPartial(b)
}

/** Shared regex for HH:MM validation — exported so callers can mark the field invalid. */
export const TIME_HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

export function isValidHHMM(value: string): boolean {
  return TIME_HHMM_REGEX.test(value)
}
